import React from 'react';
import { Chip } from '@mui/material';
import {
  Schedule as HoursIcon,
  DeviceHub as ChannelIcon,
  Inventory as ItemIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import type { AuditAction } from '@byte-store-control/shared';

interface ActionBadgeProps {
  action: AuditAction;
  size?: 'small' | 'medium';
}

interface ActionConfig {
  label: string;
  color: 'primary' | 'success' | 'error' | 'warning' | 'info' | 'default';
  icon: React.ReactElement;
}

const ACTION_CONFIG: Record<AuditAction, ActionConfig> = {
  // Store Hours
  STORE_HOURS_UPDATE: {
    label: 'Updated Hours',
    color: 'primary',
    icon: <HoursIcon fontSize="small" />,
  },
  SPECIAL_HOURS_CREATE: {
    label: 'Added Special Hours',
    color: 'primary',
    icon: <HoursIcon fontSize="small" />,
  },
  SPECIAL_HOURS_DELETE: {
    label: 'Removed Special Hours',
    color: 'warning',
    icon: <HoursIcon fontSize="small" />,
  },
  STORE_HOURS_BULK_UPDATE: {
    label: 'Bulk Hours Update',
    color: 'primary',
    icon: <HoursIcon fontSize="small" />,
  },
  SPECIAL_HOURS_BULK_CREATE: {
    label: 'Bulk Special Hours',
    color: 'primary',
    icon: <HoursIcon fontSize="small" />,
  },
  // Channels
  CHANNEL_ACTIVATE: {
    label: 'Channel On',
    color: 'success',
    icon: <ChannelIcon fontSize="small" />,
  },
  CHANNEL_DEACTIVATE: {
    label: 'Channel Off',
    color: 'error',
    icon: <ChannelIcon fontSize="small" />,
  },
  CHANNEL_BULK_UPDATE: {
    label: 'Bulk Channel Update',
    color: 'info',
    icon: <ChannelIcon fontSize="small" />,
  },
  ONLINE_ORDERS_UPDATE: {
    label: 'Online Orders Updated',
    color: 'primary',
    icon: <ChannelIcon fontSize="small" />,
  },
  // Items
  ITEM_BLACKOUT_CREATE: {
    label: 'Snoozed Item',
    color: 'warning',
    icon: <ItemIcon fontSize="small" />,
  },
  ITEM_BLACKOUT_DELETE: {
    label: 'Item Available',
    color: 'success',
    icon: <ItemIcon fontSize="small" />,
  },
  // Auth
  USER_LOGIN: {
    label: 'Logged In',
    color: 'default',
    icon: <LoginIcon fontSize="small" />,
  },
};

export const ActionBadge: React.FC<ActionBadgeProps> = ({ action, size = 'small' }) => {
  const config = ACTION_CONFIG[action] || {
    label: action,
    color: 'default' as const,
    icon: <HoursIcon fontSize="small" />,
  };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      variant="outlined"
      sx={{
        fontWeight: 500,
        '& .MuiChip-icon': {
          color: 'inherit',
        },
      }}
    />
  );
};

/**
 * Get display text for an action
 */
export function getActionDisplayText(action: AuditAction): string {
  return ACTION_CONFIG[action]?.label || action;
}

/**
 * Get color for an action
 */
export function getActionColor(action: AuditAction): ActionConfig['color'] {
  return ACTION_CONFIG[action]?.color || 'default';
}
